import {useReviewTicketsQuery} from "@/api/reviewTicketsApi";
import {buildActionItemsParams} from "@/components/AdminHub/actionItemsParams";
import ActionQueueTab from "@/components/AdminHub/ActionQueueTab";
import ActivityLogTab from "@/components/AdminHub/ActivityLogTab";
import AmbassadorsTab from "@/components/AdminHub/AmbassadorsTab";
import ViolationsTab from "@/components/AdminHub/ViolationsTab";
import clsxm from "@/lib/clsxm";
import {createFileRoute, useNavigate} from "@tanstack/react-router";
import {zodValidator} from "@tanstack/zod-adapter";
import {type FC} from "react";
import {useTranslation} from "react-i18next";
import {useTitle} from "react-use";
import {z} from "zod";

const TAB_KEYS = ["action_queue", "activity_log", "violations", "ambassadors"] as const;
type TTabKey = (typeof TAB_KEYS)[number];

const searchSchema = z.object({
  // Optional so plain links to /admins/hub need no search param; an invalid
  // value falls back rather than erroring the route.
  tab: z.enum(TAB_KEYS).optional().catch("action_queue"),
});

export const Route = createFileRoute("/_authenticated/_admin/admins/hub")({
  component: AdminHub,
  validateSearch: zodValidator(searchSchema),
});

const TABS: {key: TTabKey; Component: FC}[] = [
  {key: "action_queue", Component: ActionQueueTab},
  {key: "activity_log", Component: ActivityLogTab},
  {key: "violations", Component: ViolationsTab},
  {key: "ambassadors", Component: AmbassadorsTab},
];

// The env notice is a dev/staging affordance ("automations run here, off on prod"); it
// must never render on production. Same production check the app uses elsewhere.
const IS_PRODUCTION = import.meta.env.VITE_APP_ENV_IS_PRODUCTION === "true";

function AdminHub() {
  const {t} = useTranslation("admin_hub");

  useTitle(t("title"));

  const navigate = useNavigate();
  const {tab = "action_queue"} = Route.useSearch();
  const ActiveTab = TABS.find(item => item.key === tab)?.Component ?? TABS[0].Component;

  // Live count for the Action queue tab badge: open, action-required records awaiting a
  // decision (unfiltered, matching the queue's default view). Mirrors the count read
  // inside ActionQueueTab, so it stays in sync via the shared query cache.
  const actionQueueCountQuery = useReviewTicketsQuery(
    buildActionItemsParams({kind: "ACTION_REQUIRED", search: "", status: "OPEN", ticketTypes: []}),
  );
  const tabCounts: Partial<Record<TTabKey, number>> = {
    action_queue: actionQueueCountQuery.data?.count ?? 0,
  };

  return (
    <div className="max-w-wide container mx-auto mt-6 mb-20 flex flex-col gap-6 px-4 sm:mt-8 sm:px-8">
      {/* Header — orient the reviewer */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-neutral-black text-2xl font-bold">{t("title")}</h1>
          <p className="text-neutral-grey-dark mt-1.5 max-w-2xl text-sm">{t("subtitle")}</p>
        </div>
        {!IS_PRODUCTION && (
          <span className="bg-warning-light-yellow text-warning-dark-yellow inline-flex shrink-0 items-center gap-2 self-start rounded-full px-3 py-1 text-xs font-semibold">
            <span className="bg-warning-dark-yellow size-1.5 rounded-full" />
            {t("env_notice")}
          </span>
        )}
      </div>

      <div className="border-neutral-white-dark scrollbar-hide flex items-center gap-1 overflow-x-auto overflow-y-hidden border-b">
        {TABS.map(({key}) => {
          const count = tabCounts[key];
          return (
            <button
              key={key}
              type="button"
              onClick={() =>
                navigate({to: ".", search: prev => ({...prev, tab: key}), replace: true, resetScroll: false})
              }
              className={clsxm(
                "-mb-px inline-flex shrink-0 cursor-pointer items-center gap-2 border-b-2 bg-transparent px-4 py-2 text-sm font-semibold whitespace-nowrap transition-colors",
                tab === key
                  ? "border-primary-dark-solid text-primary-dark-solid"
                  : "text-neutral-grey-dark hover:text-neutral-black border-transparent",
              )}>
              {t(key)}
              {count ? (
                <span className="bg-primary-light-solid text-primary-dark-solid text-2xs rounded-full px-1.5 py-px font-bold">
                  {count}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      <ActiveTab />
    </div>
  );
}
